import React,
{
    forwardRef,
    Fragment,
    ReactNode,
    useContext,
    useEffect,
    useRef,
} from 'react';
import {
    Autocomplete,
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteClasses,
    AutocompleteValue,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    ListSubheader,
    TextField,
    styled,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { VariableSizeList, ListChildComponentProps } from 'react-window';

const classesPrefix = 'selectAutocomplete';

const classes = {
    formControl: `${classesPrefix}-formControl`,
    textField: `${classesPrefix}-textField`,
    option: `${classesPrefix}-option`,
};

const StyledFormControl = styled(FormControl)(() => {
    return {
        [`&.${classes.formControl}`]: {
            flexGrow: 1,
        },
        [`& .${classes.textField}`]: {
            flexGrow: 1,
            margin: 0,
        },
    };
});

const StyledDiv = styled('div')(() => {
    return {
        [`& .${classes.option}`]: {
            overflow: 'visible',
            whiteSpace: 'nowrap',
        },
    };
});

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps): React.DetailedReactHTMLElement<{ style: any; }, HTMLElement> {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: (style.top as number) + LISTBOX_PADDING,
        },
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref: React.LegacyRef<any>) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: number): React.MutableRefObject<any> {
    const ref = useRef<VariableSizeList>(null);

    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);

    return ref;
}

const VirtualizedList = React.forwardRef<HTMLDivElement>((props: any, ref) => {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: ReactNode): number => {
        if (React.isValidElement(child) && child.type === ListSubheader) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = (): number => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }

        return itemData.map(getChildSize).reduce((a, b) => {
            return a + b;
        }, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <StyledDiv ref={ref}>
            <OuterElementContext.Provider value={other}>
                {/* @ts-ignore */}
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width='100%'
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType='ul'
                    itemSize={(index): number => { return getChildSize(itemData[index]); }}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </StyledDiv>
    );
});
VirtualizedList.displayName = 'VirtualizedList';

const SelectAutocomplete = <
    T,
    Multiple extends boolean | undefined = false,
>
    ({
        label,
        value,
        options,
        multiple,
        onChange,
        getOptionLabel,
        isOptionEqualToValue,
        disabled,
        loading,
        formatChipContent,
        autocompleteClasses,
    }: {
        /**
         * Label for the TextField
         */
        label: string;
        /**
         * The value of the autocomplete.
         *
         * The value must have reference equality with the option in order to be selected.
         * You can customize the equality behavior with the `isOptionEqualToValue` prop.
         */
        value: AutocompleteValue<T, Multiple, false, false>;
        /**
         * Array of options.
         */
        options: ReadonlyArray<T>;
        /**
         * If `true`, `value` must be an array and the menu will support multiple selections.
         * @default false
         */
        multiple: Multiple;
        /**
        * Callback fired when the value changes.
        *
        * @param {React.SyntheticEvent} event The event source of the callback.
        * @param {T|T[]} value The new value of the component.
        * @param {string} reason One of "createOption", "selectOption", "removeOption", "blur" or "clear".
        * @param {string} [details]
        */
        onChange: (
            event: React.SyntheticEvent,
            value: AutocompleteValue<T, Multiple, false, false>,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<T>,
        ) => void;
        /**
         * Used to determine the string value for a given option.
         * It's used to fill the input (and the list box options if `renderOption` is not provided).
         *
         * If used in free solo mode, it must accept both the type of the options and a string.
         *
         * @param {T} option
         * @returns {string}
         */
        getOptionLabel: (option: T) => string;
        /**
         * Used to determine if the option represents the given value.
         * Uses strict equality by default.
         * ⚠️ Both arguments need to be handled, an option can only match with one value.
         *
         * @param {T} option The option to test.
         * @param {T} value The value to test against.
         * @returns {boolean}
         */
        isOptionEqualToValue: (option: T, value: T) => boolean;
        /**
         * Used to format the content of the chip if allowing multiple selections.
         * @param {T} value The value to format.
         * @returns {string}
         */
        formatChipContent: Multiple extends true ? (value: T) => string : undefined;
        /**
         * If `true`, the component is disabled.
         * @default false
         */
        disabled?: boolean;
        /**
         * If `true`, the component is in a loading state.
         * This shows the `loadingText` in place of suggestions (only if there are no suggestions to show, e.g. `options` are empty).
         * @default false
         */
        loading?: boolean;
        /**
         * Override or extend the styles applied to the component.
         */
        autocompleteClasses?: Partial<AutocompleteClasses>;
    }): React.JSX.Element => {
    return (
        <StyledFormControl className={classes.formControl}>
            <Autocomplete
                classes={autocompleteClasses ? {
                    ...autocompleteClasses,
                } : {
                    option: classes.option,
                }}
                options={options}
                ListboxComponent={VirtualizedList as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                value={value}
                loading={loading}
                renderInput={(params): React.JSX.Element => {
                    return (
                        <TextField
                            {...params}
                            className={classes.textField}
                            variant='standard'
                            label={label}
                            inputProps={{
                                ...params.inputProps,
                                autoComplete: 'off',
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <Fragment>
                                        {loading ? <CircularProgress color='inherit' size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </Fragment>
                                ),
                            }}
                        />
                    );
                }}
                renderTags={
                    multiple ?
                        (tags, getTagProps): React.JSX.Element[] => {
                            return tags.map((option, index): React.JSX.Element => {
                                return (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={`badge-${option}-${index}`}
                                        label={formatChipContent ? formatChipContent(option) : option as ReactNode}
                                        disabled={disabled}
                                    />
                                );
                            });
                        } :
                        undefined
                }
                renderOption={
                    multiple ?
                        (props, option, { selected }): React.JSX.Element => {
                            return (
                                <li {...props}>
                                    <Checkbox
                                        size='small'
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {getOptionLabel(option)}
                                </li>
                            );
                        } :
                        undefined
                }
                onChange={onChange}
                disabled={disabled}
                multiple={multiple}
                disableClearable={false}
                openOnFocus
                disableCloseOnSelect={multiple}
                filterSelectedOptions={!multiple}
                data-testid='autocomplete'
            />
        </StyledFormControl>
    );
};

export default SelectAutocomplete;
